import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AccountLayout from 'pages/account/components/AccountLayout/AccountLayout';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import {
  getAccountBalance,
  getAccountIBAN,
  getAccounts,
  getAccountsArray,
  getAccountTitle,
  getCurrentAccountObject
} from 'shared/config/store/selectors/account';
import { getAccountCards } from 'shared/config/store/reducers/cardsSlice';
import { RootState } from 'shared/config/store/store';
import {
  getEMICurrentAccountObject,
  setCurrentAccount
} from 'shared/config/store/reducers/accountsSlice';
import { FileFormat } from 'shared/model/types';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { Account } from 'entities/account/model/types';
import { ROUTES } from 'shared/const/routes';
import { useLazyGetAccountStatementQuery, downloadAccountStatement } from '../api/index';
import { useIntl } from 'react-intl';
import { CStatus } from 'entities/card/model/types';
import MoveFundsModal from '../components/MoveFundsModal/MoveFundsModal';

const AccountPage = () => {
  const { id } = useParams();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showError, showSuccess } = useMessage();
  const [getStatement] = useLazyGetAccountStatementQuery();
  const [showMoveFundsModal, setShowMoveFundsModal] = useState(false);

  const accountStatus = useSelector(getCurrentAccountObject).status;
  const accounts = useSelector(getAccounts);
  const accountsArray = useSelector(getAccountsArray);
  const accountTitle = useSelector(getAccountTitle);
  const accountIBAN = useSelector(getAccountIBAN);
  const accountBalance = useSelector(getAccountBalance);
  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account;
  const card = useSelector(getAccountCards(currentEmi, currentAccount.id))?.[0];
  const canAddCard = !card || card.status === CStatus.Closed;
  
  const language = useSelector((state: RootState) => state.app.language);
  const userMeta = useSelector((state: RootState) => state.user.meta);

  // Show "Move funds" button only if user has more than one account
  const showMoveFunds = accountsArray.length > 1;

  useEffect(() => {
    if (!id || !accounts[id]) {
      navigate(-1);
      return;
    }
    dispatch(setCurrentAccount({ emi: currentEmi, accountId: Number(id) }));
  }, [id, accounts, navigate, dispatch, currentEmi]);

  const onStatementDownload = async ({
    dateRange,
    format,
  }: {
    dateRange: string[];
    format: 'pdf' | 'csv';
  }) => {
    try {
      await downloadAccountStatement(getStatement, {
        accountId: currentAccount.id,
        dateRange: [dateRange[0], dateRange[1]],
        format: format.toUpperCase() as FileFormat,
        language: userMeta?.language || language || 'EN',
      });

      showSuccess(t({ id: 'accountPage.success' }));
    } catch (error) {
      console.error(error);
      showError(t({ id: 'accountPage.error' }));
    }
  };


  return (
    <>
      <AccountLayout
        color='linear-gradient(rgba(193, 227, 255, 1),rgba(212, 238, 238, 1))'
        title={accountTitle}
        iban={accountIBAN}
        status={accountStatus}
        card={card}
        balance={accountBalance}
        canAddCard={canAddCard}
        onTopup={() => navigate(ROUTES.TOP_UP.path)}
        onMoveFunds={() => setShowMoveFundsModal(true)}
        showMoveFunds={showMoveFunds}
        onStatementDownload={onStatementDownload}
      />
      {showMoveFunds && (
        <MoveFundsModal
          open={showMoveFundsModal}
          onClose={() => setShowMoveFundsModal(false)}
          currentAccountId={currentAccount.id}
        />
      )}
    </>
  );
};

export default AccountPage;
